import { ACCESS_TOKEN_SCHEME, authClient } from './instance';

const HTTP_STATUS_UNAUTHORIZED = 401;

/**
 * Creates an `http` object for the generated nswag clients that authorizes
 * requests as the Auth API cookie session's user.
 *
 * The token resolves to null only while the session is not authenticated, so
 * those requests are sent unauthorized; when an authenticated session cannot
 * produce a token the request fails instead, as sending it would answer as
 * nobody.
 *
 * A token can be revoked or rotated server side at any moment, and a 401 is
 * the first the client hears of it, so the request is retried once with a
 * fresh token. The 401 itself is what makes the retry safe — an unauthorized
 * request was never executed — and the generated clients send plain string
 * bodies, so there is no consumed stream to worry about.
 */
export function createAuthClientFetch(): { fetch(url: RequestInfo, init?: RequestInit): Promise<Response> } {
    return {
        fetch: async (url: RequestInfo, init?: RequestInit): Promise<Response> => {
            const accessToken = await authClient.getAccessToken();

            if (accessToken === null) {
                return window.fetch(url, init);
            }

            const response = await window.fetch(url, withAccessToken(init, accessToken));

            if (response.status !== HTTP_STATUS_UNAUTHORIZED) {
                return response;
            }

            authClient.invalidateAccessToken();

            // When no fresh token can be produced, report the 401 the request
            // actually got, not the failure to replace the token.
            let retryToken: string | null;

            try {
                retryToken = await authClient.getAccessToken();
            } catch (error: unknown) {
                console.warn('[AuthClientFetch]: Could not mint a token to retry with', error);
                retryToken = null;
            }

            if (retryToken === null) {
                return response;
            }

            return window.fetch(url, withAccessToken(init, retryToken));
        },
    };
}

/**
 * Clones the request init with the access token as the Authorization header.
 */
function withAccessToken(init: RequestInit | undefined, accessToken: string): RequestInit {
    const headers = new Headers(init?.headers);
    headers.set('Authorization', `${ACCESS_TOKEN_SCHEME} ${accessToken}`);

    return { ...init, headers };
}
